import { useState, type ReactNode } from 'react';
import { teamFilter } from '../use-teams';
import {
  UTILIZATION_COLORS,
  UTILIZATION_GROUP_TOLERANCE,
  utilizationBooked,
  utilizationLoad,
  utilizationPercent,
  type Seniority,
  type UtilizationAllocation,
  type UtilizationFigures,
  type UtilizationLoad,
  type UtilizationPerson,
  type UtilizationView,
} from '@forecastify/shared';
import {
  AvatarFace,
  Button,
  Icon,
  ListToolbar,
  Skeleton,
  StackedBars,
  Tooltip,
  type DateRange,
  LandscapeOnly,
} from '@forecastify/ui';
import { useDuration } from '../../duration';
import { useI18n, useT } from '../../i18n';
import { identityColor, initialsOf } from '../identity';
import { useAvatars } from '../use-avatar';
import { seniorityLabel } from '../people/person-screen';
import { RangeControl } from './range-control';
import { PeopleGridSkeleton } from './grid-skeleton';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 17 Oct 2026 · Utilizáció — the mockup's `dashutil`.                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `Forecastify - Utilizacios riport (standalone).html` 3205–3463, measured: a *Kihasználtság*
 * chart (200px of scale in a 224px plot), a sticky toolbar, then ONE table — a 240px person column
 * and a 104px line-label column sticky left, columns of 88px a week or 72px a day that stretch to
 * fill, 32px lines.
 *
 * ⚠️⚠️ **THE GROUPING IS DRAWN HERE, THE FIGURES ARE THE SERVER'S.** Role, seniority, skill and
 * *Felszabaduló emberek* are four arrangements of the same people, so switching between them is
 * not a request; every figure in every cell is a sum of `view.people[].figures`.
 *
 * ⚠️ **What the kinds mean is `utilization.ts`'s and `decisions.md` UT-1's** — *Kiosztott* is
 * billable + non-billable, soft is a line of its own, hatched in the chart.
 */

export type UtilizationGrouping = 'role' | 'seniority' | 'free' | 'skill';

/** 26 Oct 2026 · how many projects a bar's tooltip names before *+N others*. */
const TIP_PROJECTS = 5;

const GROUPINGS: readonly UtilizationGrouping[] = ['role', 'seniority', 'free', 'skill'];

/** Highest level first — the mockup lists Senior, Medior, Junior. */
const LEVEL_ORDER: readonly Seniority[] = ['principal', 'lead', 'senior', 'medior', 'junior'];

const LOAD_COLOR: Readonly<Record<Exclude<UtilizationLoad, 'none'>, string>> = {
  under: 'var(--load-under)',
  ok: 'var(--load-ok)',
  over: 'var(--load-over)',
};

const LEAVE = { bg: 'var(--leave)', fg: 'var(--leave-ink)' } as const;

/** The soft band: the primary colour, hatched — the mockup's `hatch`. */
const HATCH =
  'repeating-linear-gradient(135deg, var(--primary) 0 3px, color-mix(in oklch, var(--primary) 25%, transparent) 3px 7px)';

const tint = (color: string, percent: number): string =>
  `color-mix(in oklch, ${color} ${String(percent)}%, transparent)`;

interface Cell {
  readonly text: string;
  readonly className: string;
  readonly background?: string;
  readonly color?: string;
}

interface Line {
  readonly key: string;
  readonly label: string;
  readonly strong: boolean;
  readonly cells: readonly Cell[];
}

interface Group {
  readonly key: string;
  readonly title: string;
  readonly people: readonly UtilizationPerson[];
  /** In the skill grouping, which skill — its chip goes on every person row. */
  readonly skillId?: string;
}

export function UtilizationScreen(props: {
  readonly loading: boolean;
  readonly view: UtilizationView;
  readonly range: DateRange;
  readonly defaultRange: DateRange;
  readonly grouping: UtilizationGrouping;
  readonly roleId: string;
  readonly seniority: string;
  readonly projectId: string;
  readonly query: string;
  readonly onGrouping: (grouping: UtilizationGrouping) => void;
  readonly onRole: (roleId: string) => void;
  readonly onSeniority: (seniority: string) => void;
  readonly onProject: (projectId: string) => void;
  /** R-60 · the team filter — absent where the screen is embedded without one. */
  readonly teams?: readonly { readonly id: string; readonly name: string }[];
  readonly teamId?: string;
  readonly onTeam?: (id: string) => void;
  readonly onQuery: (query: string) => void;
  readonly onMode: (mode: 'week' | 'day') => void;
  readonly onRange: (range: DateRange) => void;
  readonly onClear: () => void;
  readonly onOpenPerson: (memberId: string) => void;
}) {
  const { fmt } = useDuration();
  const t = useT();
  const { locale } = useI18n();
  const { view } = props;
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const avatars = useAvatars(view.people.map((person) => person.memberAvatarUrl));

  const columns = view.columns;
  const count = Math.max(1, columns.length);
  const width = view.mode === 'day' ? 72 : 88;
  const gridColumns = `repeat(${String(count)}, minmax(${String(width)}px, 1fr))`;
  const searching = props.query.trim() !== '';

  const toggle = (key: string): void => {
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const day = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const short = (iso: string): string => day.format(new Date(`${iso}T00:00:00Z`));
  const columnTitle = (index: number): string => {
    const column = columns[index];
    if (column === undefined) return '';
    return column.from === column.to
      ? short(column.from)
      : `${short(column.from)} – ${short(column.to)}`;
  };

  /* ── the chart ─────────────────────────────────────────────────────────────────────── */

  const shares = view.bars.map((bar) => {
    const capacity = Number(bar.capacity);
    return {
      capacity,
      billable: utilizationPercent(Number(bar.billable), capacity),
      nonBillable: utilizationPercent(Number(bar.nonBillable), capacity),
      soft: utilizationPercent(Number(bar.soft), capacity),
    };
  });
  /* ⚠️ The mockup's scale: at least 100%, stepped by 25 up to 125 and by 50 past it. */
  const peak = Math.max(100, ...shares.map((s) => s.billable + s.nonBillable + s.soft));
  const step = peak <= 125 ? 25 : 50;
  const max = Math.ceil(peak / step) * step;
  const ticks: { value: number; label: string }[] = [];
  for (let value = 0; value <= max; value += step)
    ticks.push({ value, label: `${String(value)}%` });
  /* Under fourteen labels, however many columns — the mockup's `lblEvery`. */
  const labelEvery = Math.ceil(count / 14);

  const tipRow = (swatch: string, label: string, value: string, round = false): ReactNode => (
    <div key={label} className="flex items-center gap-1.5">
      <span
        aria-hidden
        className={`size-2 shrink-0 ${round ? 'rounded-full' : 'rounded-[2px]'}`}
        style={{ background: swatch }}
      />
      <span className={round ? 'flex-1 truncate' : 'text-muted-foreground flex-1'}>{label}</span>
      <span className={`tabular-nums ${round ? 'text-muted-foreground' : ''}`}>{value}</span>
    </div>
  );

  const bars = view.bars.map((bar, index) => {
    const share = shares[index] ?? { capacity: 0, billable: 0, nonBillable: 0, soft: 0 };
    const hard = Number(bar.billable) + Number(bar.nonBillable);
    const free = Math.max(0, share.capacity - hard);
    const value = (percent: number, hours: number): string =>
      t('util.tip.value', { percent: Math.round(percent), hours: fmt(hours) });
    return {
      key: columns[index]?.key ?? String(index),
      label: index % labelEvery === 0 ? short(columns[index]?.from ?? '') : '',
      top: share.capacity > 0 ? `${String(Math.round(share.billable + share.nonBillable))}%` : '–',
      segments: [
        { key: 'billable', value: share.billable, fill: UTILIZATION_COLORS.billable },
        { key: 'nonBillable', value: share.nonBillable, fill: UTILIZATION_COLORS.nonBillable },
        { key: 'soft', value: share.soft, fill: HATCH },
      ],
      tooltip: (
        <>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[12.5px] font-medium">{columnTitle(index)}</span>
            <span className="text-muted-foreground text-[11px] tabular-nums">
              {t('util.tip.capacity', { hours: fmt(share.capacity) })}
            </span>
          </div>
          {tipRow(
            UTILIZATION_COLORS.billable,
            t('util.legend.billable'),
            value(share.billable, Number(bar.billable)),
          )}
          {tipRow(
            UTILIZATION_COLORS.nonBillable,
            t('util.legend.nonBillable'),
            value(share.nonBillable, Number(bar.nonBillable)),
          )}
          {tipRow(HATCH, t('util.legend.soft'), value(share.soft, Number(bar.soft)))}
          {tipRow(
            'var(--muted)',
            t('util.tip.free'),
            value(utilizationPercent(free, share.capacity), free),
          )}
          {bar.projects.length === 0 ? null : <div className="bg-border h-px" />}
          {/* 26 Oct 2026 · the five largest, then the rest as one line — a busy week listed
              thirty projects and the tooltip ran off the screen. The API sorts by hours. */}
          {bar.projects
            .slice(0, TIP_PROJECTS)
            .map((project) =>
              tipRow(project.color, project.name, fmt(Number(project.hours)), true),
            )}
          {bar.projects.length <= TIP_PROJECTS
            ? null
            : tipRow(
                'transparent',
                t('util.tip.others', { count: bar.projects.length - TIP_PROJECTS }),
                fmt(
                  bar.projects
                    .slice(TIP_PROJECTS)
                    .reduce((sum, project) => sum + Number(project.hours), 0),
                ),
                true,
              )}
        </>
      ),
    };
  });

  /* ── the table ─────────────────────────────────────────────────────────────────────── */

  const cell = (
    hours: number,
    kind: 'plain' | 'mute' | 'soft' | 'softText' | 'leave' | 'blank' | UtilizationLoad,
  ): Cell => {
    const text = kind === 'blank' ? '' : fmt(hours);
    if (kind === 'mute' || kind === 'none') {
      return { text, className: 'text-muted-foreground/45' };
    }
    if (kind === 'plain' || kind === 'blank') return { text, className: '' };
    if (kind === 'soft') {
      return {
        text,
        className: 'font-medium',
        background: tint('var(--primary)', 12),
        color: 'var(--primary)',
      };
    }
    if (kind === 'softText') return { text, className: '', color: 'var(--primary)' };
    if (kind === 'leave') {
      return { text, className: 'font-medium', background: tint(LEAVE.bg, 14), color: LEAVE.fg };
    }
    const color = LOAD_COLOR[kind];
    return { text, className: 'font-medium', background: tint(color, 12), color };
  };

  /** The four lines of a group or a person — the mockup's `linesFor`. */
  const linesFor = (people: readonly UtilizationPerson[], tolerance: number): Line[] => {
    const sum = (index: number, pick: (figures: UtilizationFigures) => number): number =>
      people.reduce((total, person) => {
        const figures = person.figures[index];
        return figures === undefined ? total : total + pick(figures);
      }, 0);
    const capacity = columns.map((_, i) => sum(i, (f) => Number(f.capacity)));
    const hard = columns.map((_, i) => sum(i, (f) => utilizationBooked(f)));
    const soft = columns.map((_, i) => sum(i, (f) => Number(f.soft)));
    const leave = columns.map((_, i) => sum(i, (f) => Number(f.leave)));

    const lines: Line[] = [
      {
        key: 'capacity',
        label: t('resource.line.capacity'),
        strong: false,
        cells: capacity.map((hours) => cell(hours, hours > 0 ? 'plain' : 'mute')),
      },
      {
        key: 'booked',
        label: t('resource.line.allocated'),
        strong: true,
        cells: hard.map((hours, i) =>
          cell(hours, utilizationLoad(hours, capacity[i] ?? 0, tolerance)),
        ),
      },
    ];
    if (soft.some((hours) => hours > 0)) {
      lines.push({
        key: 'soft',
        label: t('util.line.soft'),
        strong: false,
        cells: soft.map((hours) => cell(hours, hours > 0 ? 'soft' : 'blank')),
      });
    }
    lines.push({
      key: 'leave',
      label: t('resource.line.leave'),
      strong: false,
      cells: leave.map((hours) => cell(hours, hours > 0 ? 'leave' : 'blank')),
    });
    return lines;
  };

  const face = (person: UtilizationPerson): ReactNode => {
    const avatar = avatars.get(person.memberAvatarUrl ?? '');
    return avatar === undefined ? (
      <span
        aria-hidden
        className="text-on-id mt-0.5 grid size-6 shrink-0 place-items-center rounded-full text-[9.5px] font-semibold"
        style={{ background: identityColor(person.memberId) }}
      >
        <AvatarFace
          seed={person.memberAvatarSeed ?? undefined}
          fallback={initialsOf(person.memberName)}
          size={24}
        />
      </span>
    ) : (
      <img src={avatar} alt="" className="mt-0.5 size-6 shrink-0 rounded-full object-cover" />
    );
  };

  const overPlanned = (person: UtilizationPerson): boolean =>
    person.figures.some((figures) => utilizationBooked(figures) > Number(figures.capacity) + 0.05);

  /** The line labels and the cells — identical for a group, a person and the flat list. */
  const lineBlock = (lines: readonly Line[], ground: string): ReactNode => (
    <>
      <div className={`${ground} sticky left-[240px] z-10 w-[104px] shrink-0 border-r`}>
        {lines.map((line) => (
          <div
            key={line.key}
            className={`flex h-8 items-center justify-end px-2 text-[11.5px] font-medium ${
              line.strong ? 'text-foreground' : 'text-muted-foreground'
            }`}
          >
            {line.label}
          </div>
        ))}
      </div>
      <div className={`${ground} min-w-0 flex-1`}>
        {lines.map((line) => (
          <div key={line.key} className="grid h-8" style={{ gridTemplateColumns: gridColumns }}>
            {line.cells.map((one, index) => (
              <div
                key={columns[index]?.key ?? String(index)}
                className={`flex items-center justify-end border-l px-2 text-[12px] tabular-nums ${one.className}`}
                style={{ background: one.background, color: one.color }}
              >
                {one.text}
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );

  const allocationRow = (allocation: UtilizationAllocation): ReactNode => {
    const soft = allocation.kind === 'soft';
    const tags = [
      allocation.clientName ?? t('projects.internal'),
      allocation.pmName,
      allocation.roleName,
      allocation.kind === 'nonBillable' ? t('util.alloc.nonBillable') : null,
      !soft && allocation.crossRole ? t('util.alloc.crossRole') : null,
    ].filter((tag): tag is string => tag !== null && tag !== '');
    return (
      <div key={allocation.key} className="flex border-b">
        <div className="bg-background sticky left-0 z-10 flex w-[240px] shrink-0 items-start gap-2 border-r py-2 pr-2 pl-10">
          <span
            aria-hidden
            className="mt-1.5 size-2 shrink-0 rounded-[3px]"
            style={{ background: allocation.projectColor ?? 'var(--muted-foreground)' }}
          />
          <div className="min-w-0 flex-1 leading-tight">
            <span className="flex items-center gap-1.5">
              <span className="truncate text-[12px] font-medium">
                {allocation.projectName ?? t('util.alloc.noProject')}
              </span>
              {soft ? (
                <span className="text-muted-foreground border-muted-foreground/40 shrink-0 rounded border border-dashed px-1 text-[9.5px] tracking-wide uppercase">
                  {t('util.alloc.soft')}
                </span>
              ) : null}
            </span>
            <span className="text-muted-foreground mt-0.5 block truncate text-[11px]">
              {tags.join(' · ')}
            </span>
          </div>
        </div>
        <div className="bg-background text-muted-foreground sticky left-[240px] z-10 flex w-[104px] shrink-0 items-center justify-end border-r px-2 text-[11px]">
          {soft ? t('util.line.soft') : t('resource.line.allocated')}
        </div>
        <div
          className="bg-muted/25 grid min-w-0 flex-1"
          style={{ gridTemplateColumns: gridColumns }}
        >
          {allocation.hours.map((value, index) => {
            const hours = Number(value);
            const one = cell(hours, hours === 0 ? 'mute' : soft ? 'softText' : 'plain');
            return (
              <div
                key={columns[index]?.key ?? String(index)}
                className={`flex items-center justify-end border-l px-2 text-[11.5px] tabular-nums ${one.className}`}
                style={{ color: one.color }}
              >
                {one.text}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const personRow = (person: UtilizationPerson, scope: string, skillId?: string): ReactNode => {
    const key = `p:${props.grouping}:${scope}:${person.memberId}`;
    const expanded = open.has(key);
    const skill =
      skillId === undefined ? undefined : person.skills.find((one) => one.id === skillId);
    return (
      <div key={key}>
        <div className="flex border-b">
          <div className="bg-background sticky left-0 z-10 flex w-[240px] shrink-0 items-start gap-2 border-r px-2 py-2">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('resource.row.breakdown')}
              aria-expanded={expanded}
              onClick={() => {
                toggle(key);
              }}
            >
              <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={14} />
            </Button>
            {face(person)}
            <div className="min-w-0 flex-1">
              <span className="flex items-center gap-1">
                <button
                  type="button"
                  className="truncate text-left text-[12.5px] font-medium hover:underline"
                  onClick={() => {
                    props.onOpenPerson(person.memberId);
                  }}
                >
                  {person.memberName}
                </button>
                {overPlanned(person) ? (
                  <Tooltip content={t('util.row.overPlanned')}>
                    <span
                      role="img"
                      aria-label={t('util.row.overPlanned')}
                      className="flex items-center"
                    >
                      <Icon name="triangle-alert" size={13} className="text-destructive" />
                    </span>
                  </Tooltip>
                ) : null}
              </span>
              {skill === undefined ? null : (
                <span className="border-primary/30 bg-primary/10 text-primary mt-1 inline-flex rounded border px-1.5 text-[10.5px] leading-[18px]">
                  {skill.levelName === null ? skill.name : `${skill.name} · ${skill.levelName}`}
                </span>
              )}
            </div>
          </div>
          {lineBlock(linesFor([person], 1), 'bg-background')}
        </div>
        {expanded ? person.allocations.map((allocation) => allocationRow(allocation)) : null}
      </div>
    );
  };

  /* ── the groups ────────────────────────────────────────────────────────────────────── */

  /** How much room a person has, on average, per column — the skill groups sort on it. */
  const averageFree = (person: UtilizationPerson): number =>
    person.figures.reduce(
      (total, figures) =>
        total + Math.max(0, Number(figures.capacity) - utilizationBooked(figures)),
      0,
    ) / count;

  const groups = ((): Group[] => {
    const people = view.people;
    if (props.grouping === 'role') {
      const named = view.roles.map((role) => ({
        key: role.id,
        title: role.name,
        people: people.filter((person) => person.role?.id === role.id),
      }));
      return [
        ...named,
        { key: 'none', title: t('util.noRole'), people: people.filter((p) => p.role === null) },
      ].filter((group) => group.people.length > 0);
    }
    if (props.grouping === 'seniority') {
      return [
        ...LEVEL_ORDER.map((level) => ({
          key: level,
          title: seniorityLabel(t, level),
          people: people.filter((person) => person.seniority === level),
        })),
        {
          key: 'none',
          title: t('util.noSeniority'),
          people: people.filter((person) => person.seniority === null),
        },
      ].filter((group) => group.people.length > 0);
    }
    if (props.grouping === 'skill') {
      const names = new Map<string, string>();
      for (const person of people)
        for (const skill of person.skills) names.set(skill.id, skill.name);
      return [...names]
        .map(([id, name]) => ({
          key: id,
          title: name,
          skillId: id,
          people: people
            .filter((person) => person.skills.some((skill) => skill.id === id))
            .sort((a, b) => averageFree(b) - averageFree(a)),
        }))
        .sort(
          (a, b) => b.people.length - a.people.length || a.title.localeCompare(b.title, locale),
        );
    }
    return [];
  })();

  /**
   * *Felszabaduló emberek* — everybody with room in some column, soonest first: the first column
   * under 50% booked, then the first under 100%, then the most room on average. The mockup's
   * `flat`.
   */
  const freeing = ((): UtilizationPerson[] => {
    if (props.grouping !== 'free') return [];
    const firstBelow = (person: UtilizationPerson, percent: number): number =>
      person.figures.findIndex(
        (figures) =>
          Number(figures.capacity) > 0 &&
          utilizationPercent(utilizationBooked(figures), Number(figures.capacity)) < percent,
      );
    return view.people
      .map((person) => ({ person, half: firstBelow(person, 50), under: firstBelow(person, 99.5) }))
      .filter((entry) => entry.under > -1)
      .sort(
        (a, b) =>
          (a.half < 0 ? 999 : a.half) - (b.half < 0 ? 999 : b.half) ||
          a.under - b.under ||
          averageFree(b.person) - averageFree(a.person),
      )
      .map((entry) => entry.person);
  })();

  const filters = [
    {
      key: 'role',
      /* 3 Nov 2026 · several at once, OR. *Nincs szerepkör* / *Nincs szint* are ordinary options
         (R-62, amended) — `none,<id>` is "no role or this one", ORed on the server. */
      kind: 'multi' as const,
      label: t('util.filter.role'),
      value: props.roleId,
      onChange: props.onRole,
      options: [
        ...view.roles.map((role) => ({ value: role.id, label: role.name })),
        { value: 'none', label: t('util.noRole') },
      ],
    },
    {
      key: 'seniority',
      kind: 'multi' as const,
      label: t('util.filter.seniority'),
      value: props.seniority,
      onChange: props.onSeniority,
      options: [
        ...LEVEL_ORDER.map((level) => ({ value: level, label: seniorityLabel(t, level) })),
        { value: 'none', label: t('util.noSeniority') },
      ],
    },
    {
      key: 'project',
      kind: 'multi' as const,
      label: t('dash.filter.project'),
      value: props.projectId,
      onChange: props.onProject,
      options: view.projects.map((project) => ({ value: project.id, label: project.name })),
    },
    ...(props.onTeam === undefined
      ? []
      : teamFilter(t, props.teams ?? [], props.teamId ?? '', props.onTeam)),
  ];

  const groupLabel = (grouping: UtilizationGrouping): string => {
    if (grouping === 'role') return t('util.group.role');
    if (grouping === 'seniority') return t('util.group.seniority');
    if (grouping === 'free') return t('util.group.free');
    return t('util.group.skill');
  };

  const tableEmpty = props.grouping === 'free' ? freeing.length === 0 : groups.length === 0;

  return (
    /* 18 Oct 2026 · landscape only on a phone, like the other people × time grids. */
    <LandscapeOnly message={t('mobile.rotate')}>
      <div data-screen="dash-util" className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-auto">
          {/* ── Kihasználtság ──────────────────────────────────────────────────────────── */}
          <div className="border-b px-4 pt-3 pb-3">
            <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
              <span className="text-[13px] font-medium">{t('util.chart.title')}</span>
              <div className="flex-1" />
              {[
                {
                  key: 'billable',
                  label: t('util.legend.billable'),
                  bg: UTILIZATION_COLORS.billable,
                },
                {
                  key: 'nonBillable',
                  label: t('util.legend.nonBillable'),
                  bg: UTILIZATION_COLORS.nonBillable,
                },
                { key: 'soft', label: t('util.legend.soft'), bg: HATCH },
              ].map((entry) => (
                <span
                  key={entry.key}
                  className="text-muted-foreground flex items-center gap-1.5 text-[11.5px]"
                >
                  <span
                    aria-hidden
                    className="size-2.5 shrink-0 rounded-[2px]"
                    style={{ background: entry.bg }}
                  />
                  {entry.label}
                </span>
              ))}
              <span className="text-muted-foreground flex items-center gap-1.5 text-[11.5px]">
                <span
                  aria-hidden
                  className="border-muted-foreground/60 w-3.5 shrink-0 border-t-2 border-dashed"
                />
                {t('util.legend.capacity')}
              </span>
            </div>
            {props.loading ? (
              <Skeleton className="h-[244px] w-full" />
            ) : (
              <StackedBars
                label={t('util.chart.title')}
                bars={bars}
                max={max}
                ticks={ticks}
                reference={100}
              />
            )}
          </div>

          {/* ── the toolbar, sticky over the table ───────────────────────────────────────── */}
          <div className="bg-background sticky top-0 z-30 border-b">
            <div className="flex items-center gap-2 pr-3">
              <ListToolbar
                className="flex-1 border-b-0"
                labels={{
                  search: t('util.search'),
                  filter: t('list.filter'),
                  views: t('list.views'),
                  columns: t('list.columns'),
                  clearAll: t('list.clearAll'),
                  searchOptions: t('list.searchOptions'),
                  clearFilter: (label: string) => t('list.clearFilter', { label }),
                  rangeFrom: t('list.rangeFrom'),
                  rangeTo: t('list.rangeTo'),
                  switchView: t('list.switchView'),
                  more: t('list.more'),
                  back: t('list.back'),
                  close: t('common.close'),
                  selectAll: t('list.selectAll'),
                  clearSelection: t('list.clearSelection'),
                }}
                search={props.query}
                onSearchChange={props.onQuery}
                views={GROUPINGS.map((grouping) => ({
                  value: grouping,
                  label: groupLabel(grouping),
                }))}
                view={props.grouping}
                onViewChange={(value) => {
                  const next = GROUPINGS.find((grouping) => grouping === value);
                  if (next !== undefined) props.onGrouping(next);
                }}
                filters={filters}
                viewSwitch={{
                  value: view.mode,
                  items: [
                    { value: 'week', label: t('dash.view.week') },
                    { value: 'day', label: t('dash.view.day') },
                  ],
                  onChange: (value) => {
                    props.onMode(value === 'day' ? 'day' : 'week');
                  },
                }}
                /* 17 Oct 2026 · the period rides the toolbar, so a phone places it beside the search. */
                trailing={
                  <RangeControl
                    value={props.range}
                    defaultRange={props.defaultRange}
                    stepDays={view.mode === 'day' ? 1 : 7}
                    onChange={props.onRange}
                  />
                }
              />
            </div>
          </div>

          {props.loading ? (
            <PeopleGridSkeleton />
          ) : view.people.length === 0 ? (
            <div className="text-muted-foreground flex flex-col items-center justify-center gap-2 p-8 text-center">
              <Icon name="users" size={22} className="opacity-60" />
              <span className="text-[13px]">{t('util.empty')}</span>
              <Button variant="outline" size="sm" onClick={props.onClear}>
                {t('list.clearAll')}
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div style={{ minWidth: 344 + count * width }}>
                {/* ── the heading row ─────────────────────────────────────────────────── */}
                <div className="bg-muted flex border-b">
                  <div className="bg-muted text-muted-foreground sticky left-0 z-20 flex w-[240px] shrink-0 items-center border-r px-3 text-[10.5px] font-medium tracking-wide uppercase">
                    {t('dash.column.person')}
                  </div>
                  <div className="bg-muted sticky left-[240px] z-20 w-[104px] shrink-0 border-r" />
                  <div className="grid min-w-0 flex-1" style={{ gridTemplateColumns: gridColumns }}>
                    {columns.map((column, index) => (
                      <div
                        key={column.key}
                        className={`flex flex-col justify-center gap-0.5 border-l px-2 py-1.5 ${
                          column.current
                            ? 'bg-primary/10'
                            : column.off
                              ? 'bg-muted-foreground/5'
                              : ''
                        }`}
                      >
                        <span className="truncate text-[11px] font-medium tabular-nums">
                          {columnTitle(index)}
                        </span>
                        <span className="text-muted-foreground text-[10.5px] tracking-wide uppercase">
                          {column.week === null
                            ? new Intl.DateTimeFormat(locale, {
                                weekday: 'short',
                                timeZone: 'UTC',
                              }).format(new Date(`${column.from}T00:00:00Z`))
                            : t('resource.week', { week: column.week })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {tableEmpty ? (
                  <div className="text-muted-foreground px-3 py-4 text-[12.5px]">
                    {t('util.tableEmpty')}
                  </div>
                ) : null}

                {props.grouping === 'free'
                  ? freeing.map((person) => personRow(person, 'all'))
                  : groups.map((group) => {
                      const key = `g:${props.grouping}:${group.key}`;
                      /* ⚠️ A search opens every group — the mockup's `q ? true : …`. */
                      const expanded = searching || open.has(key);
                      return (
                        <div key={key}>
                          <div className="flex border-b">
                            <div className="bg-sidebar sticky left-0 z-10 flex w-[240px] shrink-0 items-start gap-1.5 border-r px-2 py-2">
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label={t('util.row.group')}
                                aria-expanded={expanded}
                                onClick={() => {
                                  toggle(key);
                                }}
                              >
                                <Icon
                                  name={expanded ? 'chevron-down' : 'chevron-right'}
                                  size={14}
                                />
                              </Button>
                              <div className="min-w-0 flex-1 pt-[3px]">
                                <span className="flex items-center gap-1.5">
                                  <span className="truncate text-[12.5px] font-medium">
                                    {group.title}
                                  </span>
                                </span>
                              </div>
                            </div>
                            {lineBlock(
                              linesFor(group.people, UTILIZATION_GROUP_TOLERANCE),
                              'bg-sidebar',
                            )}
                          </div>
                          {expanded
                            ? group.people.map((person) =>
                                personRow(person, group.key, group.skillId),
                              )
                            : null}
                        </div>
                      );
                    })}
              </div>
            </div>
          )}
        </div>
      </div>
    </LandscapeOnly>
  );
}

/** Exported for the route's own fixture of an empty answer. */
export function emptyUtilization(): UtilizationView {
  return {
    mode: 'week',
    from: '',
    to: '',
    columns: [],
    bars: [],
    people: [],
    roles: [],
    projects: [],
  };
}
